import {
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Drawer,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { observer } from "mobx-react-lite";
import React from "react";
import * as socketio from "socket.io-client";
import Fetch from "../api/Fetch";
import Device, { DeviceInfo, PrinterStatus, ThermalStatus } from "../components/Device";
import { BACKEND_URI } from "../constants";
import AuthStore from "../stores/AuthStore";
import TuneIcon from "@mui/icons-material/Tune";
import DeviceControlsDialog from "../components/DeviceControlsDialog";
import CloseIcon from "@mui/icons-material/Close";
import Toaster from "../common/Toaster";
import { SalesContextColor } from "../model/Order";
import TableChartIcon from "@mui/icons-material/TableChart";
import { NavLink as Link } from "react-router-dom";

enum GroupBy {
    Location,
    Urgency,
}

const DeviceView = observer(() => {
    const [devices, setDevices] = React.useState<DeviceInfo[]>([]);
    const [groupBy, setGroupBy] = React.useState<GroupBy>(GroupBy.Urgency);
    const [filters, setFilters] = React.useState<number[]>([]);
    // const [filter, setFilter] = React.useState("");

    const [posRequest, setPosRequest] = React.useState<{
        identifier: number;
        location: string;
    } | null>(null);

    const [posConfig, setPosConfig] = React.useState<{
        identifier: number;
        config: DeviceInfo["configuration"];
    } | null>(null);
    const socket = React.useRef<socketio.Socket | null>(null);

    React.useEffect(() => {
        socket.current = socketio.io(BACKEND_URI, { auth: { token: AuthStore.tokenString } });
        socket.current.on("list", (list: DeviceInfo[]) => setDevices(list));
        socket.current.on("filters", (f: number[]) => setFilters(f));
        return () => {
            socket.current?.disconnect();
        };
    }, []);

    const onUpdate = (device: any) => {
        console.log("Connected");
        socket.current?.emit("update", device);
    };

    const [allControlsOpen, setAllControlsOpen] = React.useState(false);

    const onlineCount = devices.filter(
        (it) => `${it.identifier}` !== "-1" && Date.now() - Date.parse(it.checkedInAt) <= 120_000
    ).length;

    const groupedDevices = devices
        .filter((it) => filters.length === 0 || filters.includes(it.identifier))
        .reduce((a, b) => {
            if (!b.location) {
                const NO_LOCATION = "Others";
                if (a.get(NO_LOCATION)) {
                    a.get(NO_LOCATION).push(b);
                } else {
                    a.set(NO_LOCATION, [b]);
                }
            } else {
                if (a.get(b.location)) {
                    a.get(b.location).push(b);
                } else {
                    a.set(b.location, [b]);
                }
            }
            return a;
        }, new Map<string, any>());

    const config = posConfig?.config;

    return (
        <Container maxWidth="xl" sx={{ marginTop: "10px" }}>
            <Grid container>
                <Grid item sm={12}>
                    <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
                        <Box>
                            {AuthStore.hasAdmin && (
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                                    <Button onClick={() => setAllControlsOpen(true)}>
                                        <TuneIcon /> All Device Controls
                                    </Button>
                                    <Link to="/device-editor">
                                        <Button>
                                            <TableChartIcon /> Mass Edit Devices
                                        </Button>
                                    </Link>
                                </Box>
                            )}
                        </Box>
                        <Button
                            onClick={() => {
                                setGroupBy(groupBy === GroupBy.Location ? GroupBy.Urgency : GroupBy.Location);
                            }}
                            sx={{ marginRight: "5px" }}
                            color="primary"
                            variant="contained"
                        >
                            Group By {groupBy === GroupBy.Location ? "Urgency" : "Location"}
                        </Button>
                    </Box>
                </Grid>
                {groupBy === GroupBy.Urgency ? (
                    <>
                        {devices.filter(
                            (it) =>
                                (it.printerStatus !== PrinterStatus.GOOD || it.thermalStatus !== ThermalStatus.GOOD) &&
                                (filters.length === 0 || filters.includes(it.identifier))
                        ).length > 0 && (
                            <Grid item sm={12}>
                                <Typography variant="h4" color="secondary" sx={{ margin: "10px" }}>
                                    Requires Attention
                                </Typography>
                            </Grid>
                        )}
                        {devices
                            .filter(
                                (it) =>
                                    (it.printerStatus !== PrinterStatus.GOOD || it.thermalStatus !== ThermalStatus.GOOD) &&
                                    (filters.length === 0 || filters.includes(it.identifier))
                            )
                            .sort((a, b) => a.identifier - b.identifier)
                            .map((it, idx) => (
                                <Grid item md={12} sm={12} xs={12} key={`kiosk-${it.identifier}`}>
                                    <Device
                                        {...it}
                                        onUpdate={onUpdate}
                                        showLocation={true}
                                        onEdit={(pos) => setPosRequest(pos)}
                                        onSetConfig={(pos) => setPosConfig(pos)}
                                    />
                                </Grid>
                            ))}
                        {devices.filter(
                            (it) =>
                                it.printerStatus === PrinterStatus.GOOD &&
                                it.thermalStatus === ThermalStatus.GOOD &&
                                (filters.length === 0 || filters.includes(it.identifier))
                        ).length > 0 && (
                            <Grid item sm={12}>
                                <Typography variant="h4" color="primary" sx={{ margin: "10px" }}>
                                    Okay
                                </Typography>
                            </Grid>
                        )}
                        {devices
                            .filter(
                                (it) =>
                                    it.printerStatus === PrinterStatus.GOOD &&
                                    it.thermalStatus === ThermalStatus.GOOD &&
                                    (filters.length === 0 || filters.includes(it.identifier))
                            )
                            .sort((a, b) => a.identifier - b.identifier)
                            .map((it, idx) => (
                                <Grid item md={12} sm={12} xs={12} key={`kiosk-${it.identifier}-${idx}`}>
                                    <Device
                                        {...it}
                                        onUpdate={onUpdate}
                                        showLocation={true}
                                        onEdit={(pos) => setPosRequest(pos)}
                                        onSetConfig={(pos) => setPosConfig(pos)}
                                    />
                                </Grid>
                            ))}
                    </>
                ) : (
                    <>
                        {Array.from(groupedDevices.keys())
                            .sort((a, b) => a.localeCompare(b))
                            .map((key) => (
                                <>
                                    <Grid item sm={12}>
                                        <Typography variant="h4" color="secondary" sx={{ margin: "10px" }}>
                                            {key}
                                        </Typography>
                                    </Grid>
                                    {Array.from(groupedDevices, ([k, v]) =>
                                        key === k ? (
                                            <>
                                                {v
                                                    .sort((a, b) => a.identifier - b.identifier)
                                                    .map((it) => (
                                                        <Grid item md={12} sm={12} xs={12} key={`kiosk-${it.identifier}`}>
                                                            <Device
                                                                {...it}
                                                                onUpdate={onUpdate}
                                                                onEdit={(pos) => setPosRequest(pos)}
                                                                onSetConfig={(pos) => setPosConfig(pos)}
                                                            />
                                                        </Grid>
                                                    ))}
                                            </>
                                        ) : (
                                            <></>
                                        )
                                    )}
                                </>
                            ))}
                    </>
                )}
            </Grid>
            {/* <Drawer anchor="right" open={showFilters} onClose={() => setShowFilters(false)}>
                <Box sx={{ padding: "10px" }}>
                    <Typography variant="h5" fontWeight="bold" sx={{ marginBottom: "10px", width: "300px", maxWidth: "90%" }}>
                        Add POS' Filters
                    </Typography>
                    <Divider sx={{ marginBottom: "10px" }} />
                    <form onSubmit={onSubmit}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="POS #"
                            value={filter}
                            onChange={(e) => {
                                setFilter(e.target.value);
                            }}
                            type="number"
                        />
                    </form>
                    <Card sx={{ marginTop: "10px" }}>
                        <CardContent>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>POS #</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filters.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={2} sx={{ textAlign: "center" }}>
                                                No POS' filters
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {filters
                                        .sort((a: number, b: number) => a < b)
                                        .map((it: number) => (
                                            <TableRow>
                                                <TableCell>{it}</TableCell>
                                                <TableCell>
                                                    <Button variant="contained" color="error" onClick={() => onRemoveFilter(it)}>
                                                        Remove Filter
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </Box>
            </Drawer> */}
            <Dialog open={!!posRequest} fullWidth>
                <DialogTitle>Modify POS {posRequest?.identifier}</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" sx={{ padding: "10px" }}>
                        <TextField
                            label="Location"
                            value={posRequest?.location}
                            onChange={(e) => {
                                setPosRequest({ ...posRequest!, location: e.target.value });
                            }}
                        />
                        <Box display="flex" flexDirection="row" justifyContent="flex-end" sx={{ marginTop: "5px" }}>
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => {
                                    setPosRequest(null);
                                }}
                                sx={{ marginRight: "5px" }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    onUpdate(posRequest);
                                    Toaster.info("POS updated successfully!");
                                    setPosRequest(null);
                                }}
                            >
                                Save
                            </Button>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
            <Dialog open={!!posConfig} maxWidth="md" fullWidth>
                <DialogTitle>POS {posConfig?.identifier} Config</DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={() => setPosConfig(null)}
                    sx={(theme) => ({
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: theme.palette.grey[500],
                    })}
                >
                    <CloseIcon />
                </IconButton>
                {config && (
                    <DialogContent sx={{ p: 3 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} md={4}>
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                Sales Channel
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={8}>
                                            <Chip label={config.salesContext} color={SalesContextColor(config.salesContext)} variant="filled" />
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>

                            {config.salesContext === "BOXOFFICE" && (
                                <Grid item xs={12}>
                                    <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} md={4}>
                                                <Typography variant="subtitle1" fontWeight={600}>
                                                    COMPs Enabled
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={8}>
                                                <Chip
                                                    label={config.compsEnabled ? "Yes" : "No"}
                                                    color={config.compsEnabled ? "success" : "error"}
                                                    variant="filled"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>
                            )}

                            {[
                                { label: "Kiosk Mode", value: config.kioskMode },
                                { label: "Offline Mode", value: config.offline },
                                { label: "Printer Enabled", value: config.printerEnabled },
                                { label: "Square Enabled", value: config.squareEnabled },
                                { label: "Take-All Enabled", value: config.takeAllMode },
                                { label: "Master Password Set", value: config.masterPassword },
                            ].map((item) => (
                                <Grid item xs={12} sm={6} key={item.label}>
                                    <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                        <Grid container spacing={1} alignItems="center">
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                    {item.label}
                                                </Typography>
                                            </Grid>
                                            <Grid item>
                                                <Chip
                                                    label={item.value ? "Enabled" : "Disabled"}
                                                    color={item.value ? "success" : "error"}
                                                    variant="filled"
                                                    size="small"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>
                            ))}

                            <Grid item xs={12} sm={6}>
                                <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                                    <Grid container spacing={1} alignItems="center">
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                                Serial Number
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Chip label={config.serialNumber.toString()} color="primary" variant="filled" size="small" />
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                        </Grid>
                    </DialogContent>
                )}
            </Dialog>
            <DeviceControlsDialog
                target={allControlsOpen ? { kind: "group", label: "All Devices", onlineCount } : null}
                onClose={() => setAllControlsOpen(false)}
            />
        </Container>
    );
});

export default DeviceView;
